import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";

export interface SubscriptionPlan {
  id: string;
  code: string;
  name_ar: string;
  name_en: string;
  name_fr?: string | null;
  description_ar: string | null;
  description_en: string | null;
  description_fr?: string | null;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  trial_days: number;
  max_companies: number | null;
  max_users: number | null;
  max_journal_entries_monthly: number | null;
  features: string[];
  website_bullets?: { ar: string[]; en: string[]; fr: string[] } | null;
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
}

export interface WorkspaceSubscription {
  id: string;
  owner_user_id: string;
  plan_id: string;
  status: "trialing" | "active" | "past_due" | "canceled" | "expired";
  billing_cycle: "monthly" | "yearly";
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  is_orphan?: boolean;
  is_owner?: boolean;
  plan?: SubscriptionPlan;

}

/**
 * Returns the subscription that governs the workspace the user is currently in.
 *
 * The database enforces freezing per workspace (company_write_state ->
 * workspace_write_state), so the UI must follow the same scope: the open
 * workspace, never the signed-in user's personal subscription. Switching
 * workspace switches every billing surface with it.
 */
export const useSubscription = () => {
  const { user } = useAuth();
  const { currentWorkspace, loading: workspaceLoading } = useCompany();
  const workspaceId = currentWorkspace?.id ?? null;

  return useQuery({
    queryKey: ["subscription", user?.id, workspaceId],
    enabled: !!user && !workspaceLoading,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<WorkspaceSubscription | null> => {
      if (!user) return null;
      // Auto-create if missing (idempotent; reuses an existing workspace for invited members)
      await (supabase as any).rpc("ensure_user_subscription", { _user_id: user.id });

      // CompanyContext is the single source of truth for the open workspace.
      // A second, independent lookup here could disagree with it for accounts
      // that belong to several workspaces and make billing surfaces flip.
      let targetId = workspaceId;
      if (!targetId) {
        const { data: owned } = await (supabase as any)
          .from("workspace_subscriptions")
          .select("id")
          .eq("owner_user_id", user.id)
          .maybeSingle();
        targetId = owned?.id ?? null;
      }
      if (!targetId) return null;

      const { data, error } = await (supabase as any)
        .from("workspace_subscriptions")
        .select("*, plan:subscription_plans(*)")
        .eq("id", targetId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as WorkspaceSubscription;
      return { ...row, is_owner: row.owner_user_id === user.id };
    },
  });
};


export const usePlans = () => {
  return useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const { data, error } = await (supabase as any)
        .from("subscription_plans")
        .select("id, code, name_ar, name_en, description_ar, description_en, price_monthly, price_yearly, currency, trial_days, max_companies, max_users, max_journal_entries_monthly, features, website_bullets, is_active, is_default, sort_order")
        .eq("is_active", true)
        .neq("code", "paymob_test")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as SubscriptionPlan[];
    },
  });
};

export const useInvoices = (subscriptionId: string | undefined) => {
  return useQuery({
    queryKey: ["subscription-invoices", subscriptionId],
    enabled: !!subscriptionId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("subscription_invoices")
        .select("*")
        .eq("subscription_id", subscriptionId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
};

export const daysRemaining = (sub: WorkspaceSubscription | null | undefined): number | null => {
  if (!sub) return null;
  const end = new Date(sub.current_period_end).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
};

export const isSubscriptionActive = (sub: WorkspaceSubscription | null | undefined): boolean => {
  if (!sub) return false;
  return sub.status === "active" || sub.status === "trialing";
};

export type WriteState = "allowed" | "expired" | "canceled" | "past_due_locked";

/**
 * Mirrors the database decision function `workspace_write_state`:
 * writing is allowed while trialing/active, and during a 7 day grace period
 * after a payment falls past due.
 */
export const writeState = (sub: WorkspaceSubscription | null | undefined): WriteState => {
  if (!sub) return "allowed";
  if (sub.status === "trialing" || sub.status === "active") return "allowed";
  if (sub.status === "past_due") {
    const end = sub.current_period_end ? new Date(sub.current_period_end).getTime() : null;
    if (end === null) return "allowed";
    return end > Date.now() - 7 * 24 * 60 * 60 * 1000 ? "allowed" : "past_due_locked";
  }
  if (sub.status === "canceled") return "canceled";
  if (sub.status === "expired") return "expired";
  return "allowed";
};

export const isFrozen = (sub: WorkspaceSubscription | null | undefined): boolean =>
  writeState(sub) !== "allowed";

const PAST_DUE_GRACE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Days left of the 7 day payment grace period for a past_due subscription.
 * 0 once the grace period has expired and writing is locked.
 */
export const graceDaysRemaining = (sub: WorkspaceSubscription | null | undefined): number | null => {
  if (!sub || !sub.current_period_end) return null;
  const end = new Date(sub.current_period_end).getTime();
  return Math.max(0, Math.ceil((end + PAST_DUE_GRACE_MS - Date.now()) / (1000 * 60 * 60 * 24)));
};
